import { useEffect, useMemo, useState, useCallback } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import api from '../../utils/apiClient'
import AdminUserDetail from './AdminUserDetail'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const PAGE_SIZE = 20

function formatDate(value) {
	if (!value) return '—'
	try {
		return new Date(value).toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		})
	} catch {
		return '—'
	}
}

function SubscriptionBadge({ subscription, isLight }) {
	const type = subscription?.type || 'free'
	const active = Boolean(subscription?.isActive)

	const palette = isLight
		? {
			paid: 'bg-emerald-500/15 text-emerald-700 ring-emerald-500/30',
			trial: 'bg-amber-500/15 text-amber-700 ring-amber-500/30',
			free: 'bg-gray-500/15 text-gray-700 ring-gray-500/30',
		}
		: {
			paid: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
			trial: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
			free: 'bg-gray-500/15 text-gray-300 ring-gray-500/30',
		}

	return (
		<span
			className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ring-1 ${
				palette[type] || palette.free
			}`}
		>
			{type}
			{active && type !== 'free' ? '' : type === 'free' ? '' : ' (inactive)'}
		</span>
	)
}

function AdminUsers() {
	const [users, setUsers] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [page, setPage] = useState(1)
	const [totalPages, setTotalPages] = useState(1)
	const [total, setTotal] = useState(0)
	const [searchInput, setSearchInput] = useState('')
	const [search, setSearch] = useState('')
	const [selectedUserId, setSelectedUserId] = useState(null)
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const load = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const { data } = await api.get('/api/admin/users', {
				params: { page, limit: PAGE_SIZE, search },
			})
			setUsers(data.users || [])
			setTotalPages(data.pagination?.pages || 1)
			setTotal(data.pagination?.total || 0)
		} catch (err) {
			console.error('[admin] failed to load users:', err)
			setError(err.response?.data?.message || 'Failed to load users')
			setUsers([])
		} finally {
			setLoading(false)
		}
	}, [page, search])

	useEffect(() => {
		load()
	}, [load])

	const handleSearchSubmit = e => {
		e.preventDefault()
		setPage(1)
		setSearch(searchInput.trim())
	}

	const pageButtons = useMemo(() => {
		const buttons = []
		const max = 5
		let start = Math.max(1, page - Math.floor(max / 2))
		let end = Math.min(totalPages, start + max - 1)
		if (end - start + 1 < max) {
			start = Math.max(1, end - max + 1)
		}
		for (let i = start; i <= end; i++) buttons.push(i)
		return buttons
	}, [page, totalPages])

	return (
		<div>
			<div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
				<div>
					<h2 className={`text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>Users</h2>
					<p className={`text-xs mt-0.5 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
						{total.toLocaleString()} {total === 1 ? 'user' : 'users'} total
					</p>
				</div>
				<form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
					<div className="relative">
						<MagnifyingGlassIcon className={`h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-gray-500' : 'text-gray-400'}`} />
						<input
							value={searchInput}
							onChange={e => setSearchInput(e.target.value)}
							placeholder="Search by email"
							className={`text-sm rounded-lg ring-1 pl-8 pr-3 py-1.5 focus:outline-none focus:ring-sky-500/40 ${
								isLight
									? 'bg-white ring-gray-300 text-gray-900 placeholder-gray-400'
									: 'bg-[#15202b] ring-white/10 text-gray-200 placeholder-gray-500'
							}`}
						/>
					</div>
					<button
						type="submit"
						className="px-3 py-1.5 text-sm rounded-lg bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30 hover:bg-sky-500/25"
					>
						Search
					</button>
				</form>
			</div>

			{error && (
				<div className={`mb-3 px-3 py-2 rounded-lg bg-red-500/10 ring-1 ring-red-500/30 text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>
					{error}
				</div>
			)}

			<div className={`overflow-x-auto rounded-lg ring-1 ${isLight ? 'ring-gray-200' : 'ring-white/5'}`}>
				<table className="min-w-full text-sm">
					<thead className={`text-xs uppercase ${isLight ? 'bg-gray-50 text-gray-500' : 'bg-white/[0.03] text-gray-400'}`}>
						<tr>
							<th className="text-left px-3 py-2 font-medium">Email</th>
							<th className="text-left px-3 py-2 font-medium">User type</th>
							<th className="text-left px-3 py-2 font-medium">Subscription</th>
							<th className="text-left px-3 py-2 font-medium">Admin</th>
							<th className="text-left px-3 py-2 font-medium">Sessions</th>
							<th className="text-left px-3 py-2 font-medium">Created</th>
						</tr>
					</thead>
					<tbody className={`divide-y ${isLight ? 'divide-gray-200' : 'divide-white/5'}`}>
						{loading ? (
							<tr>
								<td colSpan={6} className={`px-3 py-8 text-center ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
									Loading…
								</td>
							</tr>
						) : users.length === 0 ? (
							<tr>
								<td colSpan={6} className={`px-3 py-8 text-center ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
									No users found
								</td>
							</tr>
						) : (
							users.map(u => (
								<tr
									key={u._id}
									onClick={() => setSelectedUserId(u._id)}
									className={`cursor-pointer ${isLight ? 'hover:bg-gray-50' : 'hover:bg-white/[0.04]'}`}
								>
									<td className={`px-3 py-2 ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>{u.email}</td>
									<td className={`px-3 py-2 capitalize ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
										{u.userType}
									</td>
									<td className="px-3 py-2">
										<SubscriptionBadge subscription={u.subscription} isLight={isLight} />
									</td>
									<td className="px-3 py-2">
										{u.isAdmin ? (
											<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30">
												admin
											</span>
										) : (
											<span className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>—</span>
										)}
									</td>
									<td className={`px-3 py-2 text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
										{u.devices?.length || 0}
									</td>
									<td className={`px-3 py-2 text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
										{formatDate(u.createdAt)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{totalPages > 1 && (
				<div className="flex items-center justify-between mt-4 text-sm">
					<div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
						Page {page} of {totalPages}
					</div>
					<div className="flex items-center gap-1">
						<button
							onClick={() => setPage(p => Math.max(1, p - 1))}
							disabled={page === 1}
							className={`px-2 py-1 rounded ring-1 disabled:opacity-40 disabled:cursor-not-allowed ${
								isLight
									? 'ring-gray-200 text-gray-700 hover:bg-gray-50'
									: 'ring-white/10 text-gray-300 hover:bg-white/5'
							}`}
						>
							<ChevronLeftIcon className="h-4 w-4" />
						</button>
						{pageButtons.map(p => (
							<button
								key={p}
								onClick={() => setPage(p)}
								className={`min-w-[2rem] px-2 py-1 rounded text-xs ${
									p === page
										? 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30'
										: isLight
											? 'text-gray-700 hover:bg-gray-50'
											: 'text-gray-300 hover:bg-white/5'
								}`}
							>
								{p}
							</button>
						))}
						<button
							onClick={() => setPage(p => Math.min(totalPages, p + 1))}
							disabled={page >= totalPages}
							className={`px-2 py-1 rounded ring-1 disabled:opacity-40 disabled:cursor-not-allowed ${
								isLight
									? 'ring-gray-200 text-gray-700 hover:bg-gray-50'
									: 'ring-white/10 text-gray-300 hover:bg-white/5'
							}`}
						>
							<ChevronRightIcon className="h-4 w-4" />
						</button>
					</div>
				</div>
			)}

			{selectedUserId && (
				<AdminUserDetail
					userId={selectedUserId}
					onClose={() => setSelectedUserId(null)}
					onChanged={load}
				/>
			)}
		</div>
	)
}

export default AdminUsers
